import React, {useEffect, useMemo, useRef, useState} from 'react';
import API from "../utils/API";
import {API_ENDPOINTS} from "../apiConfig";
import Forms from "./Forms";
import Error from "./Error";

const CURRENCY_OPTIONS = [{value: 'CZK', label: 'CZK'}, {value: 'EUR', label: 'EUR'}, {value: 'USD', label: 'USD'}];

const toNumber = (v) => (v === '' || v === null || v === undefined) ? '' : Number(v);
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const resolveAttributeDefinition = (row) => {
    const parameter = row?.parameter || null;
    return {
        key: parameter?.key || row?.key,
        label: parameter?.label || row?.label || row?.key || '',
        type: parameter?.type || row?.type || 'string',
        options: parameter?.options || row?.options || [],
        required: !!row?.required,
        sortOrder: Number(row?.sortOrder) || 0,
        active: row?.active !== false && parameter?.active !== false && parameter?.deleted !== true
    };
};

const PartVariantModal = ({close, onSaved, variant, masterPartId = null}) => {
    const inputRef = useRef(null);
    const [masters, setMasters] = useState([]);
    const [error, setError] = useState(null);
    const [priceSource, setPriceSource] = useState('net');
    const [form, setForm] = useState({
        part: null,
        name: '',
        sku: '',
        stockQuantity: 0,
        reorderThreshold: 0,
        stockPriority: 0,
        storagePlace: '',
        expectedPurchasePricePerUnit: {currency: 'CZK', net: '', gross: '', vatRate: 21},
        sellOnline: false,
        onlineVisible: false,
        onlineTitle: '',
        onlineDescription: '',
        attributes: {}
    });
    const isEdit = !!variant?._id;

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 1);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const {data} = await API.get(API_ENDPOINTS.part.list);
                setMasters(data || []);
            } catch (e) {
                setError(e);
            }
        })();
    }, []);

    const selectedMaster = useMemo(() => {
        const id = form.part?.value || masterPartId || variant?.part?._id || variant?.part;
        return (masters || []).find((m) => String(m._id) === String(id)) || null;
    }, [masters, form.part, masterPartId, variant]);

    useEffect(() => {
        if (!variant) return;
        const masterId = variant?.part?._id || variant?.part;
        setForm({
            part: masterId ? {value: masterId, label: variant?.part?.name || 'Master'} : null,
            name: variant.name || '',
            sku: variant.sku || '',
            stockQuantity: variant.stockQuantity ?? 0,
            reorderThreshold: variant.reorderThreshold ?? 0,
            stockPriority: variant.stockPriority ?? 0,
            storagePlace: variant.storagePlace || '',
            expectedPurchasePricePerUnit: {
                currency: variant.expectedPurchasePricePerUnit?.currency || 'CZK',
                net: toNumber(variant.expectedPurchasePricePerUnit?.net ?? ''),
                gross: toNumber(variant.expectedPurchasePricePerUnit?.gross ?? ''),
                vatRate: toNumber(variant.expectedPurchasePricePerUnit?.vatRate ?? 21)
            },
            sellOnline: !!variant.sellOnline,
            onlineVisible: !!variant.onlineVisible,
            onlineTitle: variant.onlineTitle || '',
            onlineDescription: variant.onlineDescription || '',
            attributes: variant.attributes || {}
        });
    }, [variant]);

    useEffect(() => {
        if (variant) return;
        if (!masterPartId) return;
        const master = (masters || []).find((m) => String(m._id) === String(masterPartId));
        if (!master) return;
        setForm((prev) => ({
            ...prev,
            part: {value: master._id, label: master.name},
            name: prev.name || master.name
        }));
    }, [masters, masterPartId, variant]);

    const onChangeNet = (v) => {
        const net = toNumber(v);
        const rate = Number(form.expectedPurchasePricePerUnit.vatRate) || 0;
        const gross = (net === '' ? '' : round2(Number(net) * (1 + rate / 100)));
        setForm((p) => ({...p, expectedPurchasePricePerUnit: {...p.expectedPurchasePricePerUnit, net, gross}}));
        setPriceSource('net');
    };
    const onChangeGross = (v) => {
        const gross = toNumber(v);
        const rate = Number(form.expectedPurchasePricePerUnit.vatRate) || 0;
        const net = (gross === '' ? '' : round2(Number(gross) / (1 + rate / 100)));
        setForm((p) => ({...p, expectedPurchasePricePerUnit: {...p.expectedPurchasePricePerUnit, net, gross}}));
        setPriceSource('gross');
    };
    const onChangeVat = (v) => {
        const vatRate = toNumber(v);
        const {net, gross} = form.expectedPurchasePricePerUnit;
        if (priceSource === 'net') {
            const nextGross = (net === '' ? '' : round2(Number(net) * (1 + Number(vatRate) / 100)));
            setForm((p) => ({...p, expectedPurchasePricePerUnit: {...p.expectedPurchasePricePerUnit, vatRate, gross: nextGross}}));
        } else {
            const nextNet = (gross === '' ? '' : round2(Number(gross) / (1 + Number(vatRate) / 100)));
            setForm((p) => ({...p, expectedPurchasePricePerUnit: {...p.expectedPurchasePricePerUnit, vatRate, net: nextNet}}));
        }
    };

    const setAttributeValue = (key, value) => {
        setForm((prev) => ({...prev, attributes: {...prev.attributes, [key]: value}}));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            const payload = {
                part: form.part?.value || selectedMaster?._id,
                name: String(form.name || '').trim(),
                sku: String(form.sku || '').trim().toUpperCase(),
                stockQuantity: Number(form.stockQuantity) || 0,
                reorderThreshold: Number(form.reorderThreshold) || 0,
                stockPriority: Number(form.stockPriority) || 0,
                storagePlace: form.storagePlace || '',
                expectedPurchasePricePerUnit: form.expectedPurchasePricePerUnit,
                sellOnline: !!form.sellOnline,
                onlineVisible: !!form.onlineVisible,
                onlineTitle: form.onlineTitle || '',
                onlineDescription: form.onlineDescription || '',
                attributes: form.attributes || {}
            };
            if (isEdit) await API.put(API_ENDPOINTS.partSku.edit(variant._id), payload);
            else await API.post(API_ENDPOINTS.partSku.add, payload);
            await onSaved?.();
            close();
        } catch (err) {
            setError(err);
        }
    };

    return (
        <form onSubmit={submit}>
            <div className="grid grid-cols-2 gap-4">
                <Forms.Select
                    label="Master produkt"
                    value={form.part}
                    options={(masters || []).map((m) => ({value: m._id, label: `${m.name} (${m.code})`}))}
                    onChange={(v) => setForm((p) => ({...p, part: v || null}))}
                    disabled={!!masterPartId}
                />
                <Forms.Text label="Název varianty" value={form.name} onChange={(v) => setForm((p) => ({...p, name: v}))} ref={inputRef}/>
                <Forms.Text label="SKU" value={form.sku} onChange={(v) => setForm((p) => ({...p, sku: v}))}/>
                <Forms.Text label="Skladové místo" value={form.storagePlace} onChange={(v) => setForm((p) => ({...p, storagePlace: v}))}/>
                <Forms.Number label="Sklad ks" value={form.stockQuantity} onChange={(v) => setForm((p) => ({...p, stockQuantity: toNumber(v)}))}/>
                <Forms.Number label="Min. sklad" value={form.reorderThreshold} onChange={(v) => setForm((p) => ({...p, reorderThreshold: toNumber(v)}))}/>
                <Forms.Number label="Priorita" value={form.stockPriority} onChange={(v) => setForm((p) => ({...p, stockPriority: toNumber(v)}))}/>
            </div>

            <div className="mt-5 border rounded-md p-3">
                <div className="font-medium mb-2">Parametry varianty</div>
                <div className="grid grid-cols-2 gap-4">
                    {(selectedMaster?.variantSchema || [])
                        .map(resolveAttributeDefinition)
                        .filter((it) => it.active && it.key)
                        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                        .map((def) => {
                        const value = form.attributes?.[def.key];
                        if (def.type === 'boolean') {
                            return <Forms.Checkbox key={def.key} label={def.label} value={!!value} onChange={(v) => setAttributeValue(def.key, !!v)}/>;
                        }
                        if (def.type === 'enum') {
                            return (
                                <Forms.Select
                                    key={def.key}
                                    label={def.label}
                                    value={def.options?.includes(value) ? {value, label: value} : null}
                                    options={(def.options || []).map((opt) => ({value: opt, label: opt}))}
                                    onChange={(v) => setAttributeValue(def.key, v?.value || null)}
                                />
                            );
                        }
                        if (def.type === 'number') {
                            return <Forms.Number key={def.key} label={def.label} value={value ?? ''} onChange={(v) => setAttributeValue(def.key, Number(v))}/>;
                        }
                        return <Forms.Text key={def.key} label={def.label} value={value ?? ''} onChange={(v) => setAttributeValue(def.key, v)}/>;
                    })}
                </div>
            </div>

            <div className="mt-5 border rounded-md p-3">
                <div className="font-medium mb-2">Nákupní cena</div>
                <div className="grid grid-cols-4 gap-3">
                    <Forms.Select label="Měna" value={CURRENCY_OPTIONS.find((x) => x.value === form.expectedPurchasePricePerUnit.currency)} options={CURRENCY_OPTIONS} onChange={(v) => setForm((p) => ({...p, expectedPurchasePricePerUnit: {...p.expectedPurchasePricePerUnit, currency: v?.value || 'CZK'}}))}/>
                    <Forms.Number label="Net" value={form.expectedPurchasePricePerUnit.net} onChange={onChangeNet}/>
                    <Forms.Number label="DPH %" value={form.expectedPurchasePricePerUnit.vatRate} onChange={onChangeVat}/>
                    <Forms.Number label="Gross" value={form.expectedPurchasePricePerUnit.gross} onChange={onChangeGross}/>
                </div>
            </div>

            <div className="mt-5 border rounded-md p-3 grid grid-cols-2 gap-3">
                <Forms.Checkbox label="Prodej online" value={!!form.sellOnline} onChange={(v) => setForm((p) => ({...p, sellOnline: !!v}))}/>
                <Forms.Checkbox label="Online viditelné" value={!!form.onlineVisible} onChange={(v) => setForm((p) => ({...p, onlineVisible: !!v}))}/>
                <Forms.Text label="Online název" value={form.onlineTitle} onChange={(v) => setForm((p) => ({...p, onlineTitle: v}))}/>
                <Forms.Text label="Online popis" value={form.onlineDescription} onChange={(v) => setForm((p) => ({...p, onlineDescription: v}))}/>
            </div>

            {error ? <Error error={error}/> : null}
            <Forms.Button submit className="mt-4" label={isEdit ? 'Uložit variantu' : 'Vytvořit variantu'}/>
        </form>
    );
};

export default PartVariantModal;
